import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/UserAvatar';
import { ContactRow } from '@/components/ContactRow';
import { formatDominicaPhone } from '@/lib/formatPhone';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertTriangle, Ban, Bot, CheckCircle2, Copy, Download,
  FileVideo, Loader2, PlayCircle, ShieldOff, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import { type Incident, CATEGORY_LABEL, SEVERITY_COLORS, SEVERITY_LABEL, STATUS_COLORS, STATUS_LABEL } from './Incidents';
import { useEvidenceSignedUrls, type ResolvedEvidence } from '@/hooks/useEvidenceSignedUrls';
import { BookingForChip } from '@/components/PassengerAge';

interface Props {
  incident: Incident;
  open: boolean;
  onClose: (refreshNeeded?: boolean) => void;
}

type ActionType = 'dismiss' | 'warning' | 'suspend' | 'escalate';

export default function IncidentDetail({ incident, open, onClose }: Props) {
  const [actionDialog, setActionDialog] = useState<ActionType | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [suspensionReason, setSuspensionReason] = useState(
    incident.description?.slice(0, 200) || ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [lightboxFile, setLightboxFile] = useState<ResolvedEvidence | null>(null);
  const [rideContext, setRideContext] = useState<any>(null);
  const [localIncident, setLocalIncident] = useState<Incident>(incident);

  // Sync when parent passes a new or updated incident. Using the full
  // object (not just id) ensures realtime UPDATE payloads from the parent
  // propagate into the local optimistic state.
  useEffect(() => { setLocalIncident(incident); }, [incident]);

  const isResolved = !!localIncident.resolved_at;
  const subjectIsSuspended = !!localIncident.subject_suspended_at;

  // Generate signed URLs for evidence — only when drawer is open
  const { resolved: evidenceFiles, loading: evidenceLoading } = useEvidenceSignedUrls(
    open ? incident.evidence_urls : null
  );

  // Fetch linked ride context
  useEffect(() => {
    if (incident.ride_id) {
      setRideContext(null);
      supabase
        .from('ride_requests')
        .select('id, origin_area, dest_area, fare_amount, status, requested_at, on_behalf_of_name, on_behalf_of_age_band, on_behalf_of_notes')
        .eq('id', incident.ride_id)
        .single()
        .then(({ data, error }) => {
          if (data) setRideContext(data);
          else if (error) setRideContext({ error: true });
        });
    }
  }, [incident.ride_id]);

  // ─── Resolve action ────────────────────────────────────────────────────────

  const handleResolve = async () => {
    if (!actionDialog) return;
    if (actionDialog === 'warning' && resolutionNote.trim().length < 3) {
      toast.error('Resolution note is required for warnings');
      return;
    }
    if (actionDialog === 'suspend' && suspensionReason.trim().length < 3) {
      toast.error('Suspension reason is required');
      return;
    }

    setSubmitting(true);
    try {
      const params: any = {
        p_id: incident.id,
        p_action: actionDialog,
      };
      if (resolutionNote.trim()) params.p_resolution_note = resolutionNote.trim();
      if (actionDialog === 'suspend' && suspensionReason.trim()) {
        params.p_suspension_reason = suspensionReason.trim();
      }

      const { data: resolveData, error } = await supabase.rpc('resolve_incident_report', params);
      if (error) throw error;
      const resolveResult = resolveData as any;
      if (resolveResult?.ok === false) throw new Error(resolveResult.error || 'Failed to resolve incident');

      const actionLabels: Record<ActionType, string> = {
        dismiss: 'Incident dismissed',
        warning: 'Warning issued',
        suspend: 'Subject suspended',
        escalate: 'Incident escalated',
      };
      toast.success(actionLabels[actionDialog]);
      setActionDialog(null);
      setResolutionNote('');
      // Optimistically update local state to show resolution history immediately
      const resolvedStatus = actionDialog === 'dismiss' ? 'dismissed' : actionDialog === 'warning' ? 'warning_issued' : actionDialog === 'suspend' ? 'suspended' : 'escalated';
      setLocalIncident((prev) => ({
        ...prev,
        resolved_at: new Date().toISOString(),
        status: resolvedStatus,
        resolution_note: resolutionNote || null,
      }));
      // Also try to refresh from backend for accurate data
      try {
        const { data: refreshed } = await supabase.rpc('list_incident_reports_admin', {
          p_limit: 500,
          p_offset: 0,
        });
        const list = refreshed?.reports ?? (Array.isArray(refreshed) ? refreshed : []);
        const updated = list.find((i: any) => i.id === incident.id);
        if (updated) setLocalIncident(updated as Incident);
      } catch {}
    } catch (err: any) {
      toast.error(err.message || 'Failed to resolve incident');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Reinstate subject ─────────────────────────────────────────────────────

  const handleReinstate = async () => {
    setSubmitting(true);
    try {
      const { data: reinstateData, error } = await supabase.rpc('set_user_suspension', {
        p_user_id: incident.subject_id,
        p_suspended: false,
        p_reason: null,
      });
      if (error) throw error;
      const reinstateResult = reinstateData as any;
      if (reinstateResult?.ok === false) throw new Error(reinstateResult.error || 'Failed to reinstate');
      toast.success('Subject reinstated');
      onClose(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to reinstate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent
          side="right"
          className="bg-[#111827] border-gray-800 text-white w-[520px] sm:max-w-[520px] overflow-y-auto"
        >
          <SheetHeader className="pb-4 border-b border-gray-800">
            <SheetTitle className="text-white flex items-center gap-3">
              <Badge className={`${SEVERITY_COLORS[localIncident.severity]} text-sm px-3 py-1`}>
                {SEVERITY_LABEL[localIncident.severity]}
              </Badge>
              <Badge className={`${STATUS_COLORS[localIncident.status]} text-sm px-3 py-1`}>
                {STATUS_LABEL[localIncident.status]}
              </Badge>
            </SheetTitle>
            <p className="text-xs text-gray-400 mt-2">
              Reported {formatDistanceToNow(new Date(localIncident.created_at), { addSuffix: true })}
              {localIncident.reporter_name && !localIncident.is_system_generated && ` by ${localIncident.reporter_name}`}
            </p>
          </SheetHeader>

          <div className="mt-5 space-y-5">
            {/* SOS auto-generated callout */}
            {incident.is_system_generated && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div className="text-xs text-red-300">
                  <p className="font-medium mb-0.5">Auto-generated SOS report</p>
                  <p className="text-red-400/80">
                    This report was auto-generated when {incident.reporter_name || 'a user'} pressed the SOS button
                    {incident.ride_id && ` during ride ${incident.ride_id.slice(0, 8)}...`}.
                  </p>
                </div>
              </div>
            )}

            {/* Subject card */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Subject (Accused)</p>
              <div className="flex items-center gap-3">
                <UserAvatar
                  size="lg"
                  name={incident.subject_name}
                  src={incident.subject_picture_url}
                  ring={incident.subject_suspended_at ? 'red' : 'none'}
                />
                <div className="flex-1">
                  <p className="text-white font-medium">{incident.subject_name || 'Unknown'}</p>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-gray-700 text-gray-500 mt-0.5">
                    {incident.subject_role}
                  </Badge>
                </div>
              </div>
              <div className="mt-3 space-y-0.5">
                <ContactRow label="Phone" value={formatDominicaPhone(incident.subject_phone)} callable />
                {incident.subject_role === 'driver' && (
                  <ContactRow label="Plate" value={incident.subject_license_plate} />
                )}
              </div>
              {subjectIsSuspended && (
                <div className="mt-3 bg-red-500/5 border border-red-500/20 rounded-md p-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ban className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-xs text-red-300">
                      Suspended since {format(new Date(incident.subject_suspended_at!), 'MMM d, yyyy')}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-emerald-400 hover:text-emerald-300 text-xs h-6 px-2"
                    onClick={handleReinstate}
                    disabled={submitting}
                  >
                    Reinstate
                  </Button>
                </div>
              )}
            </div>

            {/* Reporter card */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Reporter</p>
              {incident.is_system_generated || !incident.reporter_id ? (
                <div className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-400 text-sm">System (SOS auto-report)</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      size="md"
                      name={incident.reporter_name}
                      src={incident.reporter_picture_url}
                    />
                    <div>
                      <p className="text-white text-sm">{incident.reporter_name || 'Unknown'}</p>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-gray-700 text-gray-500">
                        {incident.reporter_role}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-2">
                    <ContactRow label="Phone" value={formatDominicaPhone(incident.reporter_phone)} callable />
                  </div>
                </>
              )}
            </div>

            {/* Description */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Description</p>
              <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">
                  {incident.description || 'No description provided.'}
                </p>
              </div>
            </div>

            {/* Category */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Category:</span>
              <span className="text-sm text-gray-300">{CATEGORY_LABEL[incident.category] || incident.category}</span>
            </div>

            {/* Evidence — signed URLs from private bucket */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Evidence</p>
              {evidenceLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                  <span className="ml-2 text-xs text-gray-500">Loading evidence...</span>
                </div>
              ) : evidenceFiles.length > 0 ? (
                <div className="grid grid-cols-3 gap-3">
                  {evidenceFiles.map((file) => (
                    <button
                      key={file.path}
                      type="button"
                      onClick={() => setLightboxFile(file)}
                      className="relative aspect-square overflow-hidden rounded-lg border border-gray-700 hover:border-gray-500 transition-colors bg-gray-800/50"
                    >
                      {file.kind === 'image' && file.signedUrl ? (
                        <img
                          src={file.signedUrl}
                          alt="Incident evidence"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                      {file.kind === 'video' ? (
                        <div className="flex h-full w-full items-center justify-center">
                          {file.signedUrl ? (
                            <video
                              src={file.signedUrl}
                              className="h-full w-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <FileVideo className="h-8 w-8 text-gray-500" />
                          )}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <PlayCircle className="h-10 w-10 text-white drop-shadow-lg" />
                          </div>
                        </div>
                      ) : null}
                      {!file.signedUrl ? (
                        <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500 px-2 text-center">
                          Preview unavailable. The file may have been removed.
                        </div>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">No photos or video attached.</p>
                </div>
              )}
            </div>

            {/* Linked ride */}
            {incident.ride_id && (
              <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Linked Ride</p>
                {rideContext?.on_behalf_of_name && (
                  <div className="mb-3">
                    <BookingForChip
                      name={rideContext.on_behalf_of_name}
                      ageBand={rideContext.on_behalf_of_age_band}
                      notes={rideContext.on_behalf_of_notes}
                    />
                  </div>
                )}
                {rideContext && !rideContext.error ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-emerald-400 font-medium">{rideContext.origin_area || 'Unknown'}</span>
                      <span className="text-gray-500">→</span>
                      <span className="text-white font-medium">{rideContext.dest_area || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      {rideContext.fare_amount != null && (
                        <span>Fare: <span className="text-white">EC${Number(rideContext.fare_amount).toFixed(2)}</span></span>
                      )}
                      {rideContext.status && (
                        <span>Status: <span className="text-white capitalize">{rideContext.status.replace('_', ' ')}</span></span>
                      )}
                      {rideContext.requested_at && (
                        <span>Requested: <span className="text-white">{formatDistanceToNow(new Date(rideContext.requested_at), { addSuffix: true })}</span></span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-600 font-mono mt-1">{incident.ride_id}</p>
                  </div>
                ) : rideContext?.error ? (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-300 font-mono">{incident.ride_id.slice(0, 12)}...</p>
                    <span className="text-xs text-gray-500">Ride details unavailable</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-300 font-mono">{incident.ride_id.slice(0, 12)}...</p>
                    <span className="text-xs text-gray-500">Loading ride details...</span>
                  </div>
                )}
              </div>
            )}

            {/* Action panel or Resolution history */}
            {isResolved ? (
              <div className="bg-gray-900/50 border border-emerald-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <p className="text-sm text-emerald-400 font-medium">Resolved</p>
                </div>
                <div className="space-y-1.5 text-xs text-gray-400">
                  <p>Action: <span className="text-gray-300">{STATUS_LABEL[localIncident.status] || localIncident.status}</span></p>
                  {localIncident.resolved_at && (
                    <p>When: <span className="text-gray-300">{format(new Date(localIncident.resolved_at), 'MMM d, yyyy h:mm a')}</span></p>
                  )}
                  {localIncident.resolved_by && (
                    <p>By: <span className="text-gray-300">{localIncident.resolved_by}</span></p>
                  )}
                  {localIncident.resolution_note && (
                    <p className="mt-2 text-gray-300 italic">"{localIncident.resolution_note}"</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="border-t border-gray-800 pt-4 space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                    onClick={() => { setActionDialog('dismiss'); setResolutionNote(''); }}
                  >
                    Dismiss
                  </Button>
                  <Button
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                    onClick={() => { setActionDialog('warning'); setResolutionNote(''); }}
                  >
                    Issue Warning
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-500 text-white"
                    onClick={() => {
                      setActionDialog('suspend');
                      setResolutionNote('');
                      setSuspensionReason(incident.description?.slice(0, 200) || '');
                    }}
                  >
                    <ShieldOff className="w-3.5 h-3.5 mr-1.5" /> Suspend
                  </Button>
                  <Button
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
                    onClick={() => { setActionDialog('escalate'); setResolutionNote(''); }}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Escalate
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Action dialogs */}
      <Dialog open={actionDialog === 'dismiss'} onOpenChange={(o) => { if (!o) setActionDialog(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Dismiss Incident</DialogTitle>
            <DialogDescription className="text-gray-400">
              Mark this incident as dismissed. No action will be taken against the subject.
            </DialogDescription>
          </DialogHeader>
          {subjectIsSuspended && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-300">
                This subject is currently auto-suspended pending review. Dismissing will lift the suspension.
              </p>
            </div>
          )}
          <div className="space-y-2 py-2">
            <Label className="text-gray-300 text-sm">Resolution note (optional)</Label>
            <Textarea
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white min-h-[60px]"
              placeholder="e.g. Investigated, no evidence of wrongdoing"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionDialog(null)} className="text-gray-400">Cancel</Button>
            <Button onClick={handleResolve} disabled={submitting} className="bg-gray-700 hover:bg-gray-600 text-white">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionDialog === 'warning'} onOpenChange={(o) => { if (!o) setActionDialog(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Issue Warning</DialogTitle>
            <DialogDescription className="text-gray-400">
              This text is internal. For now, warnings do not push to the user's app. Phase 2 will add an in-app warning notice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-gray-300 text-sm">
              Resolution note <span className="text-red-400">*</span>
            </Label>
            <Textarea
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
              placeholder="Describe the warning issued..."
            />
            {resolutionNote.length > 0 && resolutionNote.trim().length < 3 && (
              <p className="text-xs text-red-400">Note must be at least 3 characters</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionDialog(null)} className="text-gray-400">Cancel</Button>
            <Button
              onClick={handleResolve}
              disabled={submitting || resolutionNote.trim().length < 3}
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Issue Warning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionDialog === 'suspend'} onOpenChange={(o) => { if (!o) setActionDialog(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Suspend Subject</DialogTitle>
            <DialogDescription className="text-gray-400">
              This will immediately block {incident.subject_name || 'the subject'} from using the app. The suspension reason will appear on their screen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">
                Suspension reason (shown to user) <span className="text-red-400">*</span>
              </Label>
              <Textarea
                value={suspensionReason}
                onChange={(e) => setSuspensionReason(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                placeholder="Reason shown on user's suspended screen..."
              />
              {suspensionReason.length > 0 && suspensionReason.trim().length < 3 && (
                <p className="text-xs text-red-400">Reason must be at least 3 characters</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Internal note (optional)</Label>
              <Textarea
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white min-h-[60px]"
                placeholder="Internal context for other admins..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionDialog(null)} className="text-gray-400">Cancel</Button>
            <Button
              onClick={handleResolve}
              disabled={submitting || suspensionReason.trim().length < 3}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <ShieldOff className="w-4 h-4 mr-1.5" /> Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionDialog === 'escalate'} onOpenChange={(o) => { if (!o) setActionDialog(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Escalate to Authorities</DialogTitle>
            <DialogDescription className="text-gray-400">
              This logs the incident as escalated. Phase 2 will integrate with Dominica Police Force reporting. For now, copy the report ID and contact authorities directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Report ID</p>
                <p className="text-sm text-white font-mono">{incident.id}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  navigator.clipboard.writeText(incident.id);
                  toast.success('Report ID copied');
                }}
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Resolution note (optional)</Label>
              <Textarea
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white min-h-[60px]"
                placeholder="e.g. Reported to Dominica Police, case ref #..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionDialog(null)} className="text-gray-400">Cancel</Button>
            <Button
              onClick={handleResolve}
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-500 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Escalate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox — images at full size, videos with controls */}
      {lightboxFile && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-8"
          onClick={() => setLightboxFile(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            onClick={() => setLightboxFile(null)}
          >
            <X className="w-6 h-6" />
          </button>
          {/* Download button */}
          {lightboxFile.signedUrl && (
            <a
              href={lightboxFile.signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-4 right-14 text-white/70 hover:text-white flex items-center gap-1.5 text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="w-5 h-5" />
            </a>
          )}
          <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full">
            {lightboxFile.kind === 'image' && lightboxFile.signedUrl ? (
              <img
                src={lightboxFile.signedUrl}
                alt="Evidence"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            ) : lightboxFile.kind === 'video' && lightboxFile.signedUrl ? (
              <video
                src={lightboxFile.signedUrl}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] rounded-lg"
              />
            ) : (
              <div className="text-gray-400 text-center">
                <FileVideo className="w-12 h-12 mx-auto mb-2" />
                <p>Preview unavailable. The file may have been removed.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
